import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"

// QR Code Scanner Controller for Ticket Agent Check-In
// Uses device camera to scan QR codes on tickets
export default class extends Controller {
  static targets = ["scannerContainer", "result", "startButton", "stopButton", "manualInput"]
  static values = {
    scanUrl: String,
    autoRedirect: { type: Boolean, default: true }
  }

  connect() {
    this.scanner = null
    this.isScanning = false
    console.log("QR Scanner controller connected")
  }

  disconnect() {
    this.stopScanning()
  }

  async startScanning() {
    if (this.isScanning) return

    try {
      // Show the scanner container BEFORE starting (required by Html5Qrcode)
      const readerElement = document.getElementById('qr-reader')
      if (readerElement) {
        readerElement.style.display = 'block'
      }
      
      // Hide placeholder
      const placeholder = document.getElementById('scanner-placeholder')
      if (placeholder) {
        placeholder.style.display = 'none'
      }
      
      // Request camera permission and start scanner
      this.scanner = new Html5Qrcode("qr-reader")
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      }

      // Use back camera on mobile, any camera on desktop
      const cameraConfig = { facingMode: "environment" }

      await this.scanner.start(
        cameraConfig,
        config,
        this.onScanSuccess.bind(this),
        this.onScanFailure.bind(this)
      )

      this.isScanning = true
      this.updateButtonStates()
      this.showMessage("Camera active. Point at a ticket QR code.", "info")

    } catch (err) {
      console.error("Error starting scanner:", err)
      this.showMessage("Could not access camera. Please allow camera permissions or use manual entry.", "error")
    }
  }

  async stopScanning() {
    if (this.scanner && this.isScanning) {
      try {
        await this.scanner.stop()
        this.scanner.clear()
      } catch (err) {
        console.error("Error stopping scanner:", err)
      }
      this.isScanning = false
      
      // Hide the scanner container
      const readerElement = document.getElementById('qr-reader')
      if (readerElement) {
        readerElement.style.display = 'none'
      }
      
      // Show placeholder
      const placeholder = document.getElementById('scanner-placeholder')
      if (placeholder) {
        placeholder.style.display = 'block'
      }
      
      this.updateButtonStates()
    }
  }

  onScanSuccess(decodedText, decodedResult) {
    console.log("QR Code scanned:", decodedText)
    
    // Play success sound/vibration
    if (navigator.vibrate) {
      navigator.vibrate(200)
    }

    // Extract ticket number from URL or use raw text
    let ticketNumber = this.extractTicketNumber(decodedText)
    
    if (ticketNumber) {
      this.showMessage(`✅ Ticket found: ${ticketNumber}`, "success")
      
      // Stop scanning to prevent multiple reads
      this.stopScanning()
      
      // Redirect to ticket scan page
      if (this.autoRedirectValue) {
        const url = this.scanUrlValue.replace('TICKET_TOKEN', ticketNumber)
        setTimeout(() => {
          window.location.href = url
        }, 500)
      }
    } else {
      this.showMessage("Invalid QR code. Please scan a ticket QR code.", "warning")
    }
  }

  onScanFailure(error) {
    // This is called frequently when no QR code is in view - don't show errors
    // console.log("Scan error:", error)
  }

  extractTicketNumber(text) {
    // If it's a URL, extract the ticket number from the path
    // Expected formats:
    // - https://countryclubwrestling.com/admin/tickets/scan/GE-XKHAWO2HCZ
    // - https://countryclubwrestling.com/ticketagent/scan/GE-XKHAWO2HCZ
    // - GE-XKHAWO2HCZ (raw ticket number)
    
    if (text.includes('/scan/')) {
      const parts = text.split('/scan/')
      if (parts.length > 1) {
        return parts[1].split('?')[0].trim()
      }
    }
    
    // Check if it looks like a ticket number (letters, dash, alphanumeric)
    const ticketPattern = /^[A-Z]{2}-[A-Z0-9]+$/i
    if (ticketPattern.test(text.trim())) {
      return text.trim().toUpperCase()
    }
    
    // Try to extract ticket number from JSON (old format)
    try {
      const json = JSON.parse(text)
      if (json.ticket_number) {
        return json.ticket_number
      }
    } catch (e) {
      // Not JSON, ignore
    }
    
    return null
  }

  showMessage(message, type) {
    if (this.hasResultTarget) {
      this.resultTarget.innerHTML = `
        <div class="scan-message scan-message-${type}">
          ${message}
        </div>
      `
    }
  }

  updateButtonStates() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.style.display = this.isScanning ? 'none' : 'inline-flex'
    }
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.style.display = this.isScanning ? 'inline-flex' : 'none'
    }
    if (this.hasScannerContainerTarget) {
      this.scannerContainerTarget.style.display = this.isScanning ? 'block' : 'none'
    }
    
    // Toggle placeholder visibility
    const placeholder = document.getElementById('scanner-placeholder')
    if (placeholder) {
      placeholder.style.display = this.isScanning ? 'none' : 'block'
    }
  }

  // Manual ticket entry
  submitManual(event) {
    event.preventDefault()
    if (this.hasManualInputTarget) {
      const ticketNumber = this.manualInputTarget.value.trim().toUpperCase()
      if (ticketNumber) {
        const url = this.scanUrlValue.replace('TICKET_TOKEN', ticketNumber)
        window.location.href = url
      }
    }
  }
}
